/**
 * Phase 8 · C-49.2 — Requisition detail dashboard service.
 *
 * Powers the right-rail viz stack on /requisitions/:id per
 * PHASE8_WRANGLE_TRANSFORMATION §34.3.C. Each reader is keyed by
 * {org_id, requisition_id} to stay RLS-safe even if callers swap orgs.
 *
 * Applications link to requisitions through the req's created_job_id /
 * job_id — we resolve the job FK first, then fetch the pipeline slice.
 */
import { supabase } from '../client';
import type { ApplicationFunnelCounts } from '@shared/components/viz';
import type { ApplicationStatus } from '@core/enums';

export type ChannelKey = 'internal' | 'linkedin' | 'naukri' | 'peerlist' | 'other';

export interface TimeToFirstInterviewStat {
  /** Median days between application `submitted_at` and the first `interview_1_scheduled` status history event. */
  medianDays: number;
  /** Signed percentage delta vs the prior 30-day cohort (negative = faster / better). 0 when insufficient data. */
  trendPct: number;
  /** How many applications made it to an interview in the measured window. */
  sampleSize: number;
}

/** Status sets that define the funnel's cumulative buckets. */
const SHORTLISTED_SET: ReadonlySet<ApplicationStatus> = new Set<ApplicationStatus>([
  'shortlisted',
  'interview_1_scheduled',
  'interview_1_conducted',
  'interview_2_scheduled',
  'interview_2_conducted',
  'offer_extended',
  'offer_accepted',
  'placed',
]);

const INTERVIEWING_SET: ReadonlySet<ApplicationStatus> = new Set<ApplicationStatus>([
  'interview_1_scheduled',
  'interview_1_conducted',
  'interview_2_scheduled',
  'interview_2_conducted',
  'offer_extended',
  'offer_accepted',
  'placed',
]);

function ymd(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function daysBetween(aISO: string, bISO: string): number {
  const a = new Date(aISO).getTime();
  const b = new Date(bISO).getTime();
  return Math.max(0, (b - a) / 86_400_000);
}

function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1]! + sorted[mid]!) / 2
    : sorted[mid]!;
}

/** Resolve the job id linked to a requisition (created_job_id preferred). */
async function resolveJobId(orgId: string, requisitionId: string): Promise<string | null> {
  if (!orgId || !requisitionId) return null;
  const { data, error } = await supabase
    .from('ggj_b2b_requisitions')
    .select('id, created_job_id, job_id')
    .eq('org_id', orgId)
    .eq('id', requisitionId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return (data as { created_job_id: string | null; job_id: string | null }).created_job_id
    ?? (data as { job_id: string | null }).job_id
    ?? null;
}

function normalizeChannel(raw: string | null | undefined): ChannelKey {
  if (!raw) return 'other';
  const s = raw.trim().toLowerCase();
  if (s === 'internal' || s === 'referral' || s === 'employee_referral') return 'internal';
  if (s.includes('linkedin')) return 'linkedin';
  if (s.includes('naukri'))   return 'naukri';
  if (s.includes('peerlist')) return 'peerlist';
  return 'other';
}

export const requisitionDashboardService = {
  /** Cumulative funnel counts for the requisition's current applications. */
  async getFunnel(orgId: string, requisitionId: string): Promise<ApplicationFunnelCounts> {
    const jobId = await resolveJobId(orgId, requisitionId);
    if (!jobId) return { applied: 0, shortlisted: 0, interviewing: 0, hired: 0 };

    const { data, error } = await supabase
      .from('ggj_b2b_applications')
      .select('status')
      .eq('org_id', orgId)
      .eq('job_id', jobId);
    if (error) throw error;

    const rows = (data ?? []) as Array<{ status: ApplicationStatus }>;
    const counts: ApplicationFunnelCounts = { applied: 0, shortlisted: 0, interviewing: 0, hired: 0 };
    for (const r of rows) {
      counts.applied += 1;
      if (SHORTLISTED_SET.has(r.status))  counts.shortlisted  += 1;
      if (INTERVIEWING_SET.has(r.status)) counts.interviewing += 1;
      if (r.status === 'placed')          counts.hired        += 1;
    }
    return counts;
  },

  /**
   * Applications-per-day over the trailing `days` (default 14), oldest → newest.
   * Uses `submitted_at` (creation date) and bucket at UTC day boundaries.
   */
  async getAppsPerDay(orgId: string, requisitionId: string, days = 14): Promise<number[]> {
    const jobId = await resolveJobId(orgId, requisitionId);
    const series = new Array<number>(days).fill(0);
    if (!jobId) return series;

    const todayUTC = new Date();
    todayUTC.setUTCHours(0, 0, 0, 0);
    const start = new Date(todayUTC.getTime() - (days - 1) * 86_400_000);
    const startISO = start.toISOString();

    const { data, error } = await supabase
      .from('ggj_b2b_applications')
      .select('submitted_at')
      .eq('org_id', orgId)
      .eq('job_id', jobId)
      .gte('submitted_at', startISO);
    if (error) throw error;

    for (const row of (data ?? []) as Array<{ submitted_at: string | null }>) {
      if (!row.submitted_at) continue;
      const d = new Date(row.submitted_at);
      d.setUTCHours(0, 0, 0, 0);
      const idx = Math.round((d.getTime() - start.getTime()) / 86_400_000);
      if (idx >= 0 && idx < days) series[idx]! += 1;
    }
    return series;
  },

  /**
   * Median days from `submitted_at` → first `interview_1_scheduled`
   * history event, with a signed trend vs the prior 30-day cohort.
   */
  async getTimeToFirstInterview(orgId: string, requisitionId: string): Promise<TimeToFirstInterviewStat> {
    const jobId = await resolveJobId(orgId, requisitionId);
    if (!jobId) return { medianDays: 0, trendPct: 0, sampleSize: 0 };

    const { data: apps, error: appsErr } = await supabase
      .from('ggj_b2b_applications')
      .select('id, submitted_at')
      .eq('org_id', orgId)
      .eq('job_id', jobId);
    if (appsErr) throw appsErr;

    const appRows = (apps ?? []) as Array<{ id: string; submitted_at: string | null }>;
    if (appRows.length === 0) return { medianDays: 0, trendPct: 0, sampleSize: 0 };

    const appIds = appRows.map((a) => a.id);
    const submittedBy = new Map<string, string>();
    for (const a of appRows) if (a.submitted_at) submittedBy.set(a.id, a.submitted_at);

    const { data: hist, error: histErr } = await supabase
      .from('ggj_b2b_application_status_history')
      .select('application_id, to_status, changed_at')
      .in('application_id', appIds)
      .eq('to_status', 'interview_1_scheduled')
      .order('changed_at', { ascending: true });
    if (histErr) throw histErr;

    const firstInterviewBy = new Map<string, string>();
    for (const h of (hist ?? []) as Array<{ application_id: string; changed_at: string }>) {
      if (!firstInterviewBy.has(h.application_id)) firstInterviewBy.set(h.application_id, h.changed_at);
    }

    // Split into current 30d cohort and prior 30d cohort by first-interview date.
    const now = Date.now();
    const cur: number[] = [];
    const prev: number[] = [];
    const overall: number[] = [];
    for (const [appId, interviewAt] of firstInterviewBy) {
      const submittedAt = submittedBy.get(appId);
      if (!submittedAt) continue;
      const d = daysBetween(submittedAt, interviewAt);
      overall.push(d);
      const ageDays = (now - new Date(interviewAt).getTime()) / 86_400_000;
      if (ageDays <= 30) cur.push(d);
      else if (ageDays <= 60) prev.push(d);
    }

    const curMedian = cur.length > 0 ? median(cur) : median(overall);
    const prevMedian = median(prev);
    const trendPct = (prevMedian > 0 && cur.length > 0)
      ? Math.round(((curMedian - prevMedian) / prevMedian) * 100)
      : 0;

    return {
      medianDays: Math.round(curMedian * 10) / 10,
      trendPct,
      sampleSize: firstInterviewBy.size,
    };
  },

  /**
   * Channel mix from candidate.source_channel across applications for this req,
   * normalized into `internal | linkedin | naukri | peerlist | other`.
   */
  async getChannelMix(orgId: string, requisitionId: string): Promise<Record<ChannelKey, number>> {
    const zero: Record<ChannelKey, number> = { internal: 0, linkedin: 0, naukri: 0, peerlist: 0, other: 0 };
    const jobId = await resolveJobId(orgId, requisitionId);
    if (!jobId) return zero;

    const { data, error } = await supabase
      .from('ggj_b2b_applications')
      .select('candidate:ggj_b2b_candidates(source_channel)')
      .eq('org_id', orgId)
      .eq('job_id', jobId);
    if (error) throw error;

    const mix = { ...zero };
    type Row = { candidate: { source_channel: string | null } | { source_channel: string | null }[] | null };
    for (const row of (data ?? []) as Row[]) {
      const c = Array.isArray(row.candidate) ? row.candidate[0] : row.candidate;
      const ch = normalizeChannel(c?.source_channel ?? null);
      mix[ch] += 1;
    }
    return mix;
  },

  /** Latest `limit` rows from ggj_b2b_object_history scoped to this requisition. */
  async getRecentActivity(orgId: string, requisitionId: string, limit = 5) {
    if (!orgId || !requisitionId) return [];
    const { data, error } = await supabase
      .from('ggj_b2b_object_history')
      .select('id, attribute_name, old_value, new_value, action, created_at, changed_by_org_user_id')
      .eq('org_id', orgId)
      .eq('entity_type', 'requisitions')
      .eq('record_id', requisitionId)
      .order('created_at', { ascending: false })
      .limit(limit);
    if (error) throw error;
    return (data ?? []) as Array<{
      id: string;
      attribute_name: string;
      old_value: string | null;
      new_value: string | null;
      action: 'insert' | 'update' | 'delete';
      created_at: string;
      changed_by_org_user_id: string | null;
    }>;
  },
};

// Helper re-export so the consumer doesn't need to import normalizeChannel privately.
export { normalizeChannel };

// Keep a copy of today's ISO helper for tests that may want a deterministic seed.
export const __TEST_ONLY__ = { ymd, daysBetween, median };
